import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import { NextImage, cssStyles, useHistory, useOffSetTop } from '@sdf/base';
import { useRouter } from 'next/router';
import { Styles } from '@t2c/shared-fe';
import { CreditContext } from '../../../context/CreditProvider';
import { showCredits } from '../../../utils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppContext, useAuth } from '../../../hooks';
import { HEADER } from '../../../utils/constant';
import Avatar from '@mui/material/Avatar';
import Popover from '@mui/material/Popover';
import { Button, ButtonProps, Divider, Skeleton, Typography, useMediaQuery } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CustomCircularProgress } from '../../circular-progress/CustomCircularProgress';
import { CreditsUsed } from '../../credits-used-popover/CreditsUsed';
import Logo from '../../Logo';
import { BONUS_WINDOW_MS, getOrCreateBonusAnchorMs } from '../../../utils/bonusCountdown';

// ----------------------------------------------------------------------

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  height: HEADER.MOBILE_HEIGHT,
  transition: theme.transitions.create(['height', 'background-color'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  borderBottom: '3px solid',
  borderColor: theme.palette.background.sidebarBorder,
}));

const ToolbarShadowStyle = styled('div')(({ theme }) => ({
  left: 0,
  right: 0,
  bottom: 0,
  height: 24,
  zIndex: -1,
  margin: 'auto',
  borderRadius: '50%',
  position: 'absolute',
  width: `calc(100% - 48px)`,
  // boxShadow: theme.customShadows.z8,
}));

// ----------------------------------------------------------------------

interface PopoverButtonProps extends ButtonProps {
  icon?: string;
  text: string;
}

const BONUS_CREDIT_LABEL = '+1M';

export const PopoverButton: React.FC<PopoverButtonProps> = ({ icon, text, sx, ...other }) => {
  return (
    <Button
      variant="text"
      fullWidth
      sx={{
        justifyContent: 'flex-start',
        fontSize: 14,
        fontWeight: 500,
        minWidth: 200,
        px: 2,
        py: 2.7,
        color: (theme) => theme.palette.text.dashboardMenu,
        ...sx,
      }}
      startIcon={icon ? <NextImage src={icon} alt="" height={24} width={24} /> : null}
      {...other}
    >
      {text}
    </Button>
  );
};

// ----------------------------------------------------------------------

export default function MainHeader({
  backBtnHandler,
  isFullWdithHeader,
}: {
  backBtnHandler?: (isPreviousRoute: boolean) => void;
  isFullWdithHeader?: boolean;
}) {
  const { previousRoute } = useHistory();
  const [showCreditsUsed, setShowCreditsUsed] = useState(false);
  const [creditsAnchorEl, setCreditsAnchorEl] = useState<HTMLElement | null>(null);

  const handlePriceClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowCreditsUsed((prev) => !prev);
    setCreditsAnchorEl(event.currentTarget);
  };

  const handleCloseCreditsUsed = () => {
    setShowCreditsUsed(false);
    setCreditsAnchorEl(null);
  };
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOffset = useOffSetTop(HEADER.MAIN_DESKTOP_HEIGHT);
  const theme = useTheme();
  const { push } = useRouter();
  const { logout } = useAuth();
  const handlePricingClick = useCallback(() => {
    push('/subscription');
    handleCloseProfilePopover();
  }, []);

  const handleAffiliateClick = useCallback(() => {
    push('/affiliate');
    handleCloseProfilePopover();
  }, []);

  const handleMcpConnectClick = useCallback(() => {
    push('/connect-mcp');
    handleCloseProfilePopover();
  }, []);

  const {
    usableCredits,
    isFetchingCredit,
    totalCredits,
    isUserSubscribed,
    hadPreviousActiveSubscription,
  } = useContext(CreditContext);

  const { userData, isUserDataLoading } = useAppContext();
  const [bonusAnchorMs, setBonusAnchorMs] = useState<number>(() =>
    getOrCreateBonusAnchorMs({ resetOnPageLoad: true })
  );
  const [remainingMs, setRemainingMs] = useState<number>(BONUS_WINDOW_MS);

  useEffect(() => {
    if (!userData || isUserSubscribed) {
      setRemainingMs(0);
      return;
    }

    setBonusAnchorMs(getOrCreateBonusAnchorMs({ resetOnPageLoad: true }));
  }, [userData, isUserSubscribed]);

  const getRemainingMs = (anchorMs: number): number => {
    const elapsed = Date.now() - anchorMs;
    return Math.max(0, BONUS_WINDOW_MS - elapsed);
  };

  const timeLeft = useMemo(() => {
    const minutes = Math.floor(remainingMs / 60000);
    const seconds = Math.floor((remainingMs % 60000) / 1000);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }, [remainingMs]);

  const hasBonusTimeLeft =
    Boolean(userData) && !isUserSubscribed && !hadPreviousActiveSubscription && remainingMs > 0;

  useEffect(() => {
    if (!userData || isUserSubscribed) {
      setRemainingMs(0);
      return;
    }

    const ms = getRemainingMs(bonusAnchorMs);
    setRemainingMs(ms);

    if (ms <= 0) return;

    const timer = window.setInterval(() => {
      const current = getRemainingMs(bonusAnchorMs);
      setRemainingMs(current);
      if (current <= 0) window.clearInterval(timer);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [bonusAnchorMs, userData, isUserSubscribed]);

  const navigateToProfile = () => {
    push('/profile');
    handleCloseProfilePopover();
  };

  const navigateToBilling = () => {
    push('/billing-details');
    handleCloseProfilePopover();
  };

  const handleOpenProfilePopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseProfilePopover = () => {
    setAnchorEl(null);
  };

  const handleOnLogout = () => {
    logout();
    handleCloseProfilePopover();
  };

  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // const redirectToAdminPage = () => {
  //   push('/dashboard/admin');
  // };

  return (
    <AppBar color="transparent">
      <ToolbarStyle
        disableGutters
        sx={{
          px: { lg: 5 },
          ...(isOffset && {
            ...cssStyles(theme).bgBlur(),
          }),
          backgroundColor: theme.palette.background.secondary,
          width: '100%',
        }}
      >
        {backBtnHandler && !isExtraSmall && (
          <Button
            sx={{ ml: isFullWdithHeader ? '80px' : '275px' }}
            startIcon={<ArrowBackIcon />}
            variant="text"
            color="primary"
            onClick={() => backBtnHandler(!!previousRoute)}
          />
        )}
        <Container maxWidth={false} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
          <Stack direction="row" spacing={isExtraSmall ? 0 : 3} alignItems="center">
            {/* {userData?.isAdmin && (
              <Box sx={style.pricingBtn} onClick={redirectToAdminPage}>
                Admin
              </Box>
            )} */}
            {isExtraSmall && (
              <Box
                sx={{
                  position: 'absolute', // Ensures it aligns absolutely within the parent
                  left: '50px', // Pushes it to the leftmost edge
                  marginLeft: 0,
                  width: '30%',
                }}
                onClick={() => push('/dashboard')}
              >
                <Logo height={42} width={160} disabledLink />
              </Box>
            )}
            {!isExtraSmall && (
              <Box sx={style.pricingBtn} onClick={handleMcpConnectClick}>
                MCP Connect
              </Box>
            )}
            {!isExtraSmall && (
              <Box sx={style.pricingBtn} onClick={() => push('https://support.txt2create.com/')}>
                Support
              </Box>
            )}
            {!isExtraSmall && (
              <Box sx={style.pricingBtn} onClick={handleAffiliateClick}>
                Affiliate
              </Box>
            )}
            {!isExtraSmall &&
              (hasBonusTimeLeft ? (
                <Box sx={style.bonusTimerChip} onClick={handlePricingClick}>
                  <Box sx={style.bonusPulseDot} />
                  <Typography sx={style.bonusTimerLabel}>Upgrade bonus ends</Typography>
                  <Typography sx={style.bonusTimerCount}>{timeLeft}</Typography>
                  <Box sx={style.bonusCta}>{BONUS_CREDIT_LABEL}</Box>
                </Box>
              ) : (
                <Box sx={style.pricingBtn} onClick={handlePricingClick}>
                  Pricing
                </Box>
              ))}
            {!isFetchingCredit && typeof usableCredits === 'number' ? (
              <Box sx={style.newCreditButton}>
                <Button sx={style.priceButton} onClick={handlePriceClick}>
                  <CustomCircularProgress
                    value={
                      totalCredits > usableCredits ? (usableCredits / totalCredits) * 100 : 100
                    }
                    label={`${Math.ceil(showCredits(usableCredits) / 1000000)}M`}
                  />
                </Button>
              </Box>
            ) : (
              <Skeleton
                variant="circular"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
              />
            )}
            {showCreditsUsed && (
              <CreditsUsed
                anchorEl={creditsAnchorEl}
                handleClose={handleCloseCreditsUsed}
                remainingCredits={usableCredits}
                totalCredits={totalCredits}
                isUserSubscribed={isUserSubscribed}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              {!isUserDataLoading && userData && (
                <Button
                  onClick={handleOpenProfilePopover}
                  startIcon={
                    <Avatar
                      sx={style.profileIcon}
                      src={userData?.picture || ''}
                      alt="profle-image"
                    />
                  }
                  endIcon={
                    Boolean(anchorEl) ? (
                      <KeyboardArrowUpRoundedIcon sx={{ fontSize: 30 }} />
                    ) : (
                      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 30 }} />
                    )
                  }
                  sx={{
                    px: 2,

                    color: (theme) => theme.palette.text.primary,
                  }}
                >
                  {!isExtraSmall && (
                    <Typography
                      sx={{
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 100,
                      }}
                    >
                      {userData?.name}
                    </Typography>
                  )}
                </Button>
              )}

              {isExtraSmall && hasBonusTimeLeft && (
                <Box sx={style.mobileBonusWrapper} onClick={handlePricingClick}>
                  <Box sx={style.mobileBonusDot} />
                  <Typography sx={style.mobileBonusText}>{timeLeft}</Typography>
                </Box>
              )}
            </Box>
          </Stack>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseProfilePopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                border: '2px solid',
                borderColor: (theme) => theme.palette.background.border,
                backgroundColor: (theme) => theme.palette.background.secondary,
                borderRadius: 1,
              },
            }}
          >
            <Stack rowGap={0.5} my={1}>
              {isExtraSmall && (
                <PopoverButton
                  text={
                    String(userData?.name || '').length > 15
                      ? `${String(userData?.name).slice(0, 15)}...`
                      : String(userData?.name || '')
                  }
                  icon={userData?.picture || undefined}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    justifyContent: 'center',
                  }}
                />
              )}
              {isExtraSmall && <Divider />}
              <PopoverButton
                text="Profile"
                icon="/assets/icons/Profile.svg"
                onClick={navigateToProfile}
                sx={{
                  color: (theme) => theme.palette.text.primary,
                }}
              />
              <Divider />
              {isExtraSmall && (
                <PopoverButton
                  text="MCP Connect"
                  icon="/assets/icons/mcp.svg"
                  onClick={handleMcpConnectClick}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                  }}
                />
              )}
              {isExtraSmall && (
                <PopoverButton
                  text="Support"
                  icon="/assets/icons/Support.svg"
                  onClick={() => push('https://support.txt2create.com/')}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                  }}
                />
              )}
              {isExtraSmall && (
                <PopoverButton
                  text="Affiliate"
                  icon="/assets/icons/Affiliate.svg"
                  onClick={handleAffiliateClick}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                  }}
                />
              )}
              {isExtraSmall && (
                <PopoverButton
                  text={hasBonusTimeLeft ? `Bonus ${BONUS_CREDIT_LABEL} in ${timeLeft}` : 'Pricing'}
                  icon="/assets/icons/Diamond.svg"
                  onClick={handlePricingClick}
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                  }}
                />
              )}
              {isExtraSmall && <Divider />}
              <PopoverButton
                text="Billing"
                icon="/assets/icons/Billing.svg"
                onClick={navigateToBilling}
                sx={{
                  color: (theme) => theme.palette.text.primary,
                }}
              />
              <Divider />
              <PopoverButton
                text="Logout"
                icon="/assets/icons/logout.svg"
                onClick={handleOnLogout}
                sx={style.errorBtn}
                color="error"
              />
            </Stack>
          </Popover>
        </Container>
      </ToolbarStyle>
      {isOffset && <ToolbarShadowStyle />}
    </AppBar>
  );
}

export const style: Styles = {
  pricingBtn: {
    cursor: 'pointer',
    color: (theme) => theme.palette.text.primary,
    fontWeight: 400,
    fontSize: 16,
    '&:hover': {
      color: (theme) => theme.palette.background.activeBorder,
    },
  },
  creditBtn: {
    padding: '1px',
    borderRadius: 100,
    display: 'inline-block',
    background: (theme) => theme.palette.gradients.gradient4,
    backgroundClip: 'border-box',
    border: '1px solid transparent',
    minWidth: '110px',
    '& > div': {
      backgroundColor: (theme) => theme.palette.background.secondary,
      borderRadius: 'inherit',
      padding: '4px 10px',
      textAlign: 'center',
    },
  },
  profileIcon: {
    cursor: 'pointer',
    borderRadius: '50%',
    height: 30,
    width: 30,
  },
  errorBtn: {
    color: (theme) => theme.palette.error.main,
  },
  newCreditButton: {
    border: '1px solid #7B35EF',
    borderRadius: '60%',
    height: '45px',
    width: '45px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  priceButton: {
    color: '#ffffff',
  },
  remainingPrice: {
    fontWeight: 700,
    fontSize: '22px',
    lineHeight: '30px',
  },
  mobileBonusWrapper: {
    position: 'absolute',
    top: -5,
    right: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    padding: '2px 6px',
    borderRadius: '999px',
    background: (theme) => theme.palette.background.activeBorder,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
    animation: 'blinkPulse 1.2s infinite',
    cursor: 'pointer',

    '@keyframes blinkPulse': {
      '0%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.6, transform: 'scale(0.9)' },
      '100%': { opacity: 1, transform: 'scale(1)' },
    },
  },

  mobileBonusDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    backgroundColor: '#fff',
  },

  mobileBonusText: {
    fontSize: 10,
    fontWeight: 700,
  },
  bonusTimerChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    cursor: 'pointer',
    borderRadius: '999px',
    border: (theme) => `1px solid ${alpha(theme.palette.background.activeBorder, 0.55)}`,
    background: (theme) =>
      `linear-gradient(90deg, ${alpha(theme.palette.background.activeBorder, 0.24)}, ${alpha(
        theme.palette.text.whatsNew,
        0.2
      )})`,
    px: { xs: 0.9, md: 1.2, lg: 1.4 },
    py: { xs: 0.55, md: 0.65, lg: 0.75 },
    minHeight: { xs: 30, md: 32, lg: 34 },
    transition: 'all 0.25s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      borderColor: (theme) => alpha(theme.palette.background.activeBorder, 0.85),
      boxShadow: (theme) => `0 8px 18px ${alpha(theme.palette.background.activeBorder, 0.28)}`,
    },
  },
  bonusPulseDot: {
    width: { xs: 6, lg: 7 },
    height: { xs: 6, lg: 7 },
    borderRadius: '50%',
    backgroundColor: (theme) => theme.palette.text.whatsNewText,
    flexShrink: 0,
    animation: 'bonusPulse 1.2s infinite',
    '@keyframes bonusPulse': {
      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.4, transform: 'scale(0.7)' },
    },
  },
  bonusTimerLabel: {
    fontSize: { md: 10, lg: 11 },
    color: (theme) => alpha(theme.palette.common.white, 0.64),
    whiteSpace: 'nowrap',
  },
  bonusTimerCount: {
    fontSize: { xs: 11, md: 12, lg: 13 },
    fontWeight: 700,
    color: (theme) => theme.palette.text.whatsNewText,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: 0.15,
    minWidth: { xs: 34, md: 38 },
    textAlign: 'right',
  },
  bonusCta: {
    borderRadius: '999px',
    backgroundColor: (theme) => theme.palette.background.activeBorder,
    color: (theme) => theme.palette.common.white,
    px: { xs: 0.75, md: 1 },
    py: { xs: 0.1, md: 0.2 },
    lineHeight: 1.4,
    fontSize: { xs: 10, md: 10.5, lg: 11 },
    fontWeight: 700,
    minWidth: { xs: 30, md: 36 },
    textAlign: 'center',
  },
};
